/**
 * Multi-video per QR code via `allvideosid` from:
 *   VerifyCamiCode, GetCamiCode, GetCamiCodesListWithPagination
 */

import {
  extractApiVideoDuration,
  extractApiVideoTimestamp,
  extractApiVideoTitle,
  getBearerToken,
  getVideoDetails,
  getVideoStatus,
} from "./ApiVideo";

function parseTimestamp(value) {
  if (value == null || value === "") return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function normalizeComparableText(value) {
  return value ? String(value).trim().toLowerCase() : "";
}

/** Skip labels / short strings mistakenly stored in allvideosid (e.g. game names). */
export function isLikelyVideoId(id, row) {
  const value = String(id ?? "").trim();
  if (!value || value.length < 8) return false;
  if (!/^[a-zA-Z0-9_-]+$/.test(value)) return false;

  const blocked = [
    row?.title,
    row?.gameName,
    row?.vName,
    row?.subject,
    row?.camiCode,
  ].map(normalizeComparableText);

  return !blocked.includes(normalizeComparableText(value));
}

function rawAllVideosId(source) {
  if (!source) return null;
  return (
    source.allvideosid ??
    source.allVideosId ??
    source.AllVideosId ??
    source.allvideosId ??
    source.allVideosID ??
    null
  );
}

function isPlaceholderVideoTitle(title) {
  const text = String(title ?? "").trim();
  if (!text) return true;
  if (/^video\s+\d+$/i.test(text)) return true;
  if (/^qrtagvideo$/i.test(text)) return true;
  if (text.toLowerCase() === "file") return true;
  return false;
}

function pickEntryDisplayName(item) {
  if (!item || typeof item !== "object") {
    return "";
  }
  const candidates = [
    item.vName,
    item.VName,
    item.videoName,
    item.VideoName,
    item.title,
    item.Title,
    item.name,
    item.Name,
    item.subject,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text && !isPlaceholderVideoTitle(text)) {
      return text;
    }
  }
  return "";
}

function coerceVideoEntry(item) {
  if (item == null || item === "") {
    return null;
  }
  if (typeof item === "string" || typeof item === "number") {
    const videoId = String(item).trim();
    return videoId ? { videoId } : null;
  }
  if (typeof item === "object") {
    const videoId = String(item.videoId ?? item.videoID ?? item.VideoId ?? item.id ?? "").trim();
    if (!videoId) {
      return null;
    }
    const displayName = pickEntryDisplayName(item);
    return {
      videoId,
      title: displayName,
      vName: displayName,
      createdAt: item.createdAt ?? item.createdDate ?? item.date ?? item.publishedAt ?? null,
      videoLength: item.videoLength ?? item.video_length ?? item.length ?? null,
      duration: item.duration ?? null,
    };
  }
  return null;
}

function parseAllVideoEntries(source) {
  const raw = rawAllVideosId(source);
  if (raw == null || raw === "") {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw.map(coerceVideoEntry).filter(Boolean);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    if (trimmed.startsWith("[")) {
      try {
        return parseAllVideoEntries({ allvideosid: JSON.parse(trimmed) });
      } catch {
        /* fall through to delimiter split */
      }
    }
    return trimmed
      .split(/[,;|]/)
      .map((part) => coerceVideoEntry(part.trim()))
      .filter(Boolean);
  }

  if (typeof raw === "number") {
    return [{ videoId: String(raw) }];
  }

  return [];
}

/** Read allvideosid from API payload (supports common casing). */
export function parseAllVideosId(source) {
  return parseAllVideoEntries(source)
    .map((entry) => entry.videoId)
    .filter(Boolean);
}

function toDurationSeconds(value) {
  const total = Number(value);
  if (!Number.isFinite(total) || total <= 0) {
    return null;
  }
  // Upload payloads sometimes store milliseconds (e.g. 45000).
  if (total > 1000) {
    return Math.round(total / 1000);
  }
  return Math.round(total);
}

function resolveVideoDurationSeconds(item) {
  const explicitLength = item?.videoLength ?? item?.video_length ?? item?.length;
  const fromLength = toDurationSeconds(explicitLength);
  if (fromLength) {
    return fromLength;
  }

  const total = toDurationSeconds(item?.duration);
  if (!total) {
    return null;
  }
  // Cami row `duration` is the QR recording limit (29s / 180s / 900s / 1200s), not clip length.
  if (total === 29 || total === 180 || total === 900 || total === 1200) {
    return null;
  }
  return total;
}

function resolveVideoDisplayName(item, index) {
  const candidates = [
    item?.vName,
    item?.title,
    item?.ctaTitle,
    item?.subject,
    item?.name,
  ];
  for (const value of candidates) {
    const text = String(value ?? "").trim();
    if (text && !isPlaceholderVideoTitle(text)) {
      return text;
    }
  }
  return `Video ${index + 1}`;
}

export function normalizeVideoEntry(item, index) {
  const videoId = String(item?.videoId ?? item?.videoID ?? "").trim();
  return {
    id: item?.id ?? videoId ?? `video-${index}`,
    videoId,
    title: resolveVideoDisplayName(item, index),
    createdAt: item?.createdAt ?? item?.createdDate ?? item?.date ?? null,
    duration: resolveVideoDurationSeconds(item),
    orderIndex: typeof item?.orderIndex === "number" ? item.orderIndex : index,
  };
}

/** Build playable video list from a cami row / verify response using allvideosid. */
export function videosFromCamiListRow(row) {
  if (!row) return [];

  const fromAllVideos = parseAllVideoEntries(row);
  const primaryId = String(row?.videoId ?? row?.videoID ?? "").trim();
  const nested = row.videos ?? row.videoList ?? row.insights;
  const rowName = String(row?.vName || row?.subject || "").trim();

  let orderedEntries = [...fromAllVideos];
  if (primaryId && !orderedEntries.some((entry) => entry.videoId === primaryId)) {
    orderedEntries = [{ videoId: primaryId }, ...orderedEntries];
  }
  if (orderedEntries.length === 0 && Array.isArray(nested) && nested.length > 0) {
    return nested
      .map((item, index) =>
        normalizeVideoEntry(
          {
            ...item,
            vName: item?.vName || item?.title || rowName,
            title: item?.title || rowName,
          },
          index
        )
      )
      .filter((v) => v.videoId);
  }
  if (orderedEntries.length === 0 && primaryId) {
    orderedEntries = [{ videoId: primaryId }];
  }

  const seen = new Set();
  const uniqueEntries = orderedEntries.filter((entry) => {
    if (!entry?.videoId || seen.has(entry.videoId) || !isLikelyVideoId(entry.videoId, row)) {
      return false;
    }
    seen.add(entry.videoId);
    return true;
  });

  return uniqueEntries.map((entry, index) => {
    const isPrimary = entry.videoId === primaryId;
    const useRowName = isPrimary || uniqueEntries.length === 1;
    return normalizeVideoEntry(
      {
        videoId: entry.videoId,
        vName: entry.vName || (useRowName ? rowName : ""),
        title: entry.title || (useRowName ? rowName : ""),
        createdAt:
          entry.createdAt ||
          (useRowName ? row?.createdAt ?? row?.createdDate ?? row?.date : null),
        videoLength: entry.videoLength ?? (useRowName ? row?.videoLength : null),
        duration: entry.duration ?? null,
        orderIndex: index,
      },
      index
    );
  });
}

/** Keep each clip's name when a new recording is saved onto the same QR code. */
export function buildAllVideosIdPayload(existingRow, nextVideo) {
  const namesById = new Map();
  const orderedIds = [];

  const remember = (videoId, name) => {
    const id = String(videoId ?? "").trim();
    if (!id) return;
    const text = String(name ?? "").trim();
    if (!namesById.has(id)) {
      orderedIds.push(id);
      namesById.set(id, text);
      return;
    }
    if (text && isPlaceholderVideoTitle(namesById.get(id))) {
      namesById.set(id, text);
    }
  };

  parseAllVideoEntries(existingRow || {}).forEach((entry) => {
    remember(entry.videoId, entry.vName || entry.title);
  });
  remember(
    existingRow?.videoId ?? existingRow?.videoID,
    existingRow?.vName || existingRow?.subject
  );
  remember(nextVideo?.videoId, nextVideo?.vName || nextVideo?.title);

  return JSON.stringify(
    orderedIds.map((videoId) => {
      const vName = namesById.get(videoId) || "";
      return vName ? { videoId, vName, title: vName } : { videoId };
    })
  );
}

export function groupListRowsByCamiCode(rows) {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const camiCode = row?.camiCode != null ? String(row.camiCode).trim() : "";
    const uniqueId =
      row?.uniqueId != null ? String(row.uniqueId).replace(/ /g, "").trim() : "";
    if (!camiCode || !uniqueId) return;

    const key = `${camiCode}::${uniqueId}`;
    const rowVideos = videosFromCamiListRow(row);

    if (!map.has(key)) {
      map.set(key, {
        camiCode,
        uniqueId,
        title: row?.vName || row?.subject || row?.title || row?.gameName || camiCode,
        gameName: row?.gameName ?? null,
        videos: [],
      });
    }

    const group = map.get(key);
    if (isPlaceholderVideoTitle(group.title) && !isPlaceholderVideoTitle(row?.vName)) {
      group.title = String(row.vName).trim();
    }
    rowVideos.forEach((video) => {
      const existing = group.videos.find((v) => v.videoId === video.videoId);
      if (!existing) {
        group.videos.push(video);
        return;
      }
      if (isPlaceholderVideoTitle(existing.title) && !isPlaceholderVideoTitle(video.title)) {
        existing.title = video.title;
      }
      if (!existing.createdAt && video.createdAt) {
        existing.createdAt = video.createdAt;
      }
      if (!existing.duration && video.duration) {
        existing.duration = video.duration;
      }
    });
  });

  return Array.from(map.values())
    .map((group) => {
      const sortedVideos = [...group.videos];
      return {
        ...group,
        videos: sortedVideos,
        latestVideo: sortedVideos[0] ?? null,
      };
    })
    .sort((a, b) => {
      const ta = parseTimestamp(a.latestVideo?.createdAt) ?? 0;
      const tb = parseTimestamp(b.latestVideo?.createdAt) ?? 0;
      return tb - ta;
    });
}

export function findLatestPlayableVideo(rows) {
  const groups = groupListRowsByCamiCode(rows);
  let best = null;
  let bestIndex = -1;

  groups.forEach((group) => {
    group.videos.forEach((video, index) => {
      if (!video.videoId) return;
      const t = parseTimestamp(video.createdAt) ?? index;
      if (t >= bestIndex) {
        bestIndex = t;
        best = {
          camiCode: group.camiCode,
          uniqueId: group.uniqueId,
          videoId: video.videoId,
        };
      }
    });
  });

  if (best) return best;

  const fallback = groups.find((g) => g.camiCode && g.uniqueId && g.latestVideo?.videoId);
  if (fallback) {
    return {
      camiCode: fallback.camiCode,
      uniqueId: fallback.uniqueId,
      videoId: fallback.latestVideo.videoId,
    };
  }
  return null;
}

function normalizeUserId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** True when a cami list row belongs to the logged-in user (coach/creator). */
export function rowBelongsToUser(row, profile) {
  if (!row || !profile) return false;

  const profileUserId = normalizeUserId(profile.id ?? profile.userId);
  if (profileUserId != null) {
    const ownerIds = [
      row.videoCreatedBy,
      row.camiCreatedBy,
      row.createdBy,
      row.userId,
    ]
      .map(normalizeUserId)
      .filter((id) => id != null);
    if (ownerIds.includes(profileUserId)) {
      return true;
    }
  }

  const profileEmail = normalizeComparableText(profile.email);
  if (profileEmail) {
    const ownerEmails = [row.email, row.vEmail, row.foreverEmail, row.videoEmail].map(
      normalizeComparableText
    );
    if (ownerEmails.includes(profileEmail)) {
      return true;
    }
  }

  return false;
}

/** Latest playable video limited to cami codes owned by the current user. */
export function findLatestPlayableVideoForUser(rows, profile) {
  const ownedRows = (Array.isArray(rows) ? rows : []).filter((row) =>
    rowBelongsToUser(row, profile)
  );
  if (ownedRows.length === 0) {
    return null;
  }
  return findLatestPlayableVideo(ownedRows);
}

export function countVideosInGroups(groups) {
  return (groups || []).reduce((sum, g) => sum + (g.videos?.length || 0), 0);
}

function pickLatestVideoEntry(videos) {
  const list = Array.isArray(videos) ? videos.filter((v) => v?.videoId) : [];
  if (list.length === 0) return null;

  let best = list[0];
  let bestTs = parseTimestamp(best.createdAt) ?? 0;
  list.forEach((video, index) => {
    const ts = parseTimestamp(video.createdAt) ?? index;
    if (ts >= bestTs) {
      bestTs = ts;
      best = video;
    }
  });
  return best;
}

/** Latest playable video id from a single verify/list row (uses allvideosid). */
export function findLatestVideoFromCamiRow(row) {
  if (!row) return null;
  const latest = pickLatestVideoEntry(videosFromCamiListRow(row));
  if (latest?.videoId) return latest.videoId;

  const fallback = String(row?.videoId ?? row?.videoID ?? "").trim();
  return fallback && isLikelyVideoId(fallback, row) ? fallback : null;
}

/**
 * Enrich videos with real upload timestamps (+ duration) from api.video.
 * Falls back to the row-level createdAt when details cannot be fetched.
 */
export async function enrichVideosWithTimestamps(videos) {
  const list = (Array.isArray(videos) ? videos : []).filter((v) => v?.videoId);
  if (list.length === 0) {
    return [];
  }

  const needsDetails = list.filter(
    (video) => !video.createdAt || !video.duration || isPlaceholderVideoTitle(video.title)
  );
  if (needsDetails.length === 0) {
    return [...list];
  }

  try {
    const bearerToken = await getBearerToken();
    const enriched = await Promise.all(
      list.map(async (video) => {
        const needsTitle = isPlaceholderVideoTitle(video.title);
        if (video.createdAt && video.duration && !needsTitle) {
          return video;
        }
        try {
          const details = await getVideoDetails(bearerToken, video.videoId);
          const timestamp = extractApiVideoTimestamp(details);
          const remoteTitle = extractApiVideoTitle(details);
          let duration = extractApiVideoDuration(details);
          if (!duration && !video.duration) {
            try {
              const status = await getVideoStatus(bearerToken, video.videoId);
              duration = extractApiVideoDuration(status);
            } catch {
              /* status duration is optional */
            }
          }
          if (!timestamp && !duration && !remoteTitle) {
            return video;
          }
          return {
            ...video,
            title: needsTitle && remoteTitle ? remoteTitle : video.title,
            createdAt: timestamp || video.createdAt || null,
            duration: duration || video.duration || null,
          };
        } catch {
          return video;
        }
      })
    );
    return enriched;
  } catch {
    return [...list];
  }
}

/**
 * Latest playable video id from a single row using real api.video upload times.
 * Prefer this over the sync findLatestVideoFromCamiRow when a network call is acceptable.
 */
export async function resolveLatestVideoIdFromCamiRow(row) {
  const list = videosFromCamiListRow(row).filter((v) => v?.videoId);
  if (list.length === 0) {
    return null;
  }
  if (list.length === 1) {
    return list[0].videoId;
  }

  const enriched = await enrichVideosWithTimestamps(list);
  let best = enriched[0];
  let bestTs = parseTimestamp(best?.createdAt) ?? 0;
  enriched.forEach((video, index) => {
    const ts = parseTimestamp(video.createdAt) ?? index;
    if (ts >= bestTs) {
      bestTs = ts;
      best = video;
    }
  });
  return best?.videoId || null;
}

/** Latest playable video entry within a grouped cami code. */
export function findLatestVideoInGroup(group) {
  return pickLatestVideoEntry(group?.videos);
}

export function buildWatchUrl({ camiCode, uniqueId, videoId, redir, email, playConfirmed }) {
  const params = new URLSearchParams();
  params.set("c", camiCode);
  params.set("p", String(uniqueId).replace(/ /g, ""));
  if (videoId) params.set("v", videoId);
  if (redir) params.set("redir", redir);
  if (email) params.set("e", email);
  if (playConfirmed) params.set("play", "1");
  return `/watch?${params.toString()}`;
}

export function buildWatchListUrl({ camiCode, videoId } = {}) {
  const params = new URLSearchParams();
  if (camiCode) params.set("c", camiCode);
  if (videoId) params.set("v", videoId);
  const qs = params.toString();
  return qs ? `/my-match-list?${qs}` : "/my-match-list";
}

export function buildPreviousVideosUrl({
  camiCode,
  uniqueId,
  currentVideoId,
  redir,
  email,
}) {
  const params = new URLSearchParams();
  params.set("c", camiCode);
  params.set("p", String(uniqueId).replace(/ /g, ""));
  if (currentVideoId) params.set("v", currentVideoId);
  if (redir) params.set("redir", redir);
  if (email) params.set("e", email);
  return `/previous-videos?${params.toString()}`;
}
